import {
  NJButton,
  NJFormItem,
  NJInlineMessage,
  NJMultiSelectItem,
  NJMultiSelectRoot,
  NJSelectItem,
  NJSelectRoot,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useState } from "react";
import { errorMessage } from "@/api/apiError";
import {
  type FormErrors,
  isBlank,
  isTooLong,
  isValidUrl,
  lengthHint,
  tooLongMessage,
} from "@/components/form/validation";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { SkillCompatibilityBrandIcon } from "./SkillCompatibilityBrandIcon";
import { SKILL_COMPATIBILITY_OPTIONS } from "./skillCompatibility";
import {
  SKILL_NAME_MAX_LENGTH,
  SKILL_SHORT_DESCRIPTION_MAX_LENGTH,
} from "./skillLimits";
import {
  type SkillProposalInput,
  useCreateSkillProposal,
  useSkillCategories,
} from "./useSkills";

type FieldKey = "name" | "shortDescription" | "repositoryUrl" | "zipFile";

type FormState = {
  name: string;
  shortDescription: string;
  fullDescription: string;
  includedSkills: string[];
  compatibleHarnesses: string[];
  sourceType: "ZIP" | "GITHUB";
  repositoryUrl: string;
};

const INITIAL_STATE: FormState = {
  name: "",
  shortDescription: "",
  fullDescription: "",
  includedSkills: [],
  compatibleHarnesses: [],
  sourceType: "ZIP",
  repositoryUrl: "",
};

const REQUIRED_MESSAGE = "This field is required.";

/** Skill proposal form — ZIP upload or GitHub source; the proposal stays `pending` until an
 * admin approves it. Field-level validation with required markers; only genuine server errors
 * surface an inline banner. */
export function SkillProposalForm({
  onSubmitted,
}: {
  onSubmitted: (skillName: string) => void;
}) {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors<FieldKey>>({});
  const createProposal = useCreateSkillProposal();
  const { data: categoryOptions } = useSkillCategories();
  const isGithub = form.sourceType === "GITHUB";

  const categoryChoices = Array.from(
    new Set([...(categoryOptions ?? []), ...form.includedSkills]),
  ).sort((a, b) => a.localeCompare(b));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function validate(): FormErrors<FieldKey> {
    const next: FormErrors<FieldKey> = {};
    const fail = (field: FieldKey, message: string) => {
      next[field] = message;
    };
    if (isBlank(form.name)) fail("name", REQUIRED_MESSAGE);
    else if (isTooLong(form.name, SKILL_NAME_MAX_LENGTH))
      fail("name", tooLongMessage(form.name, SKILL_NAME_MAX_LENGTH));
    if (isBlank(form.shortDescription))
      fail("shortDescription", REQUIRED_MESSAGE);
    else if (
      isTooLong(form.shortDescription, SKILL_SHORT_DESCRIPTION_MAX_LENGTH)
    )
      fail(
        "shortDescription",
        tooLongMessage(
          form.shortDescription,
          SKILL_SHORT_DESCRIPTION_MAX_LENGTH,
        ),
      );
    if (isGithub) {
      if (isBlank(form.repositoryUrl)) {
        fail("repositoryUrl", REQUIRED_MESSAGE);
      } else if (!isValidUrl(form.repositoryUrl)) {
        fail("repositoryUrl", "Enter a valid http(s) URL.");
      }
    } else if (!zipFile) {
      fail("zipFile", "A ZIP file is required.");
    }
    return next;
  }

  async function handleSubmit() {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    const input: SkillProposalInput = {
      name: form.name,
      shortDescription: form.shortDescription,
      fullDescription: form.fullDescription || undefined,
      includedSkills: form.includedSkills,
      compatibleHarnesses: form.compatibleHarnesses,
      sourceType: form.sourceType,
      repositoryUrl: isGithub
        ? form.repositoryUrl.trim() || undefined
        : undefined,
      zipFile: isGithub ? undefined : (zipFile ?? undefined),
    };
    const submittedName = form.name.trim();
    await createProposal.mutateAsync(input);
    setForm(INITIAL_STATE);
    setZipFile(null);
    setErrors({});
    onSubmitted(submittedName);
  }

  return (
    <div className="grid w-full gap-4 border border-nj-neutral-subtle-default p-6 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-4">
      <NJFormItem
        id="skill-proposal-name"
        label="Name"
        isRequired
        hasError={Boolean(errors.name)}
        subscriptMessage={errors.name}
        value={form.name}
        onChange={(e) => set("name", (e.target as HTMLInputElement).value)}
      />
      <NJFormItem
        id="skill-proposal-short-description"
        label="Short description"
        isRequired
        hasError={Boolean(errors.shortDescription)}
        subscriptMessage={
          errors.shortDescription ??
          lengthHint(form.shortDescription, SKILL_SHORT_DESCRIPTION_MAX_LENGTH)
        }
        value={form.shortDescription}
        onChange={(e) =>
          set("shortDescription", (e.target as HTMLInputElement).value)
        }
      />

      <div className="sm:col-span-2">
        <NJFormItem
          id="skill-proposal-full-description"
          label="Full description (markdown)"
          isMultiline
          value={form.fullDescription}
          onChange={(e) =>
            set("fullDescription", (e.target as HTMLTextAreaElement).value)
          }
        />
      </div>

      <div className="flex flex-col gap-1 border border-nj-neutral-subtle-default bg-nj-neutral-secondary-default p-3 sm:col-span-2">
        <NJText
          as="span"
          scale="xs"
          className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
        >
          Preview
        </NJText>
        {form.fullDescription.trim() ? (
          <MarkdownMessage content={form.fullDescription} />
        ) : (
          <NJText
            as="span"
            scale="sm"
            className="text-nj-neutral-tertiary-default"
          >
            No markdown preview yet.
          </NJText>
        )}
      </div>

      <NJMultiSelectRoot
        id="skill-proposal-included-skills"
        label="Categories"
        value={form.includedSkills}
        placeholder="Select categories"
        listNavigationLabel="Use arrow keys and Enter to toggle a category"
        maxTagsToDisplay={5}
        tagColor="brand"
        onChange={(nextValue) => set("includedSkills", nextValue)}
      >
        {categoryChoices.map((option) => (
          <NJMultiSelectItem key={option} value={option} label={option}>
            {option}
          </NJMultiSelectItem>
        ))}
      </NJMultiSelectRoot>

      <NJMultiSelectRoot
        id="skill-proposal-compatible-harnesses"
        label="Compatible with"
        value={form.compatibleHarnesses}
        placeholder="Select compatible harnesses"
        listNavigationLabel="Use arrow keys and Enter to toggle a harness"
        maxTagsToDisplay={5}
        tagColor="brand"
        onChange={(nextValue) => set("compatibleHarnesses", nextValue)}
      >
        {SKILL_COMPATIBILITY_OPTIONS.map((option) => (
          <NJMultiSelectItem key={option} value={option} label={option}>
            <span className="inline-flex items-center gap-2">
              <SkillCompatibilityBrandIcon value={option} />
              <span>{option}</span>
            </span>
          </NJMultiSelectItem>
        ))}
      </NJMultiSelectRoot>

      <div className="sm:col-span-2">
        <NJSelectRoot
          id="skill-proposal-source-type"
          label="Source"
          value={form.sourceType}
          listNavigationLabel="Use the arrows and Enter to choose the source"
          buttonDefaultValueLabel="Choose a source"
          onChange={(newValue) => {
            const nextSourceType = (newValue ?? "ZIP") as "ZIP" | "GITHUB";
            setForm((prev) => ({
              ...prev,
              sourceType: nextSourceType,
              repositoryUrl: nextSourceType === "ZIP" ? "" : prev.repositoryUrl,
            }));
            setZipFile(null);
          }}
        >
          <NJSelectItem value="ZIP">ZIP file</NJSelectItem>
          <NJSelectItem value="GITHUB">GitHub repository</NJSelectItem>
        </NJSelectRoot>
      </div>

      <div className="flex flex-col gap-2 sm:col-span-2">
        {isGithub ? (
          <NJFormItem
            id="skill-proposal-repository-url"
            label="Repository URL"
            isRequired
            hasError={Boolean(errors.repositoryUrl)}
            subscriptMessage={errors.repositoryUrl}
            value={form.repositoryUrl}
            onChange={(e) =>
              set("repositoryUrl", (e.target as HTMLInputElement).value)
            }
          />
        ) : (
          <label className="flex flex-col gap-1">
            <NJText as="span" scale="sm" className="font-nj-bold">
              ZIP file *
            </NJText>
            <input
              type="file"
              accept=".zip"
              onChange={(event) => setZipFile(event.target.files?.[0] ?? null)}
            />
            {errors.zipFile && (
              <NJText as="span" scale="xs" variant="danger">
                {errors.zipFile}
              </NJText>
            )}
          </label>
        )}
        <NJText
          as="span"
          scale="sm"
          className="text-nj-neutral-secondary-default"
        >
          {isGithub
            ? "GitHub skills do not require a ZIP file or version upload."
            : "Version 1 is created automatically and remains hidden until admin validation."}
        </NJText>
      </div>

      {createProposal.isError && (
        <div className="sm:col-span-2">
          <NJInlineMessage variant="error">
            {errorMessage(
              createProposal.error,
              "The server could not process this proposal. Please try again.",
            )}
          </NJInlineMessage>
        </div>
      )}

      <NJButton
        className="sm:col-span-2 sm:justify-self-start"
        label="Submit proposal"
        disabled={createProposal.isPending}
        onClick={() => void handleSubmit()}
      />
    </div>
  );
}
